'use client';

import { BrandLogo } from '@/components/BrandLogo';
import type { Dict } from '@/lib/locales/zh';
import { fetchMeClient } from '@/lib/me-client';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { type FormEvent, useEffect, useState } from 'react';

/**
 * V2.11 (2026-05-21c) — in-app 6-digit code login.
 *
 * Per Leon: clicking a magic link from a PWA bounces you out of the
 * standalone app. New flow: server emails a code, user types it back
 * into the same page. No leaving the app.
 *
 *   Step 'email'   → POST /auth/email/start → 'code'
 *   Step 'code'    → POST /auth/email/verify-code
 *                     - state=logged_in   → redirect_to
 *                     - state=invite_needed → 'invite'
 *   Step 'invite'  → POST /auth/email/complete-signup
 *                     - state=logged_in   → redirect_to
 *                     - OR join waitlist  → 'waitlist_done'
 *
 * Display name (称呼) is collected post-login at /onboarding/name —
 * not on the login form.
 */
type LoginDict = Dict['login'];
// 2026-06-23 — waitlist + invite-code steps removed. The flow is now
// just: email → code → either log in or auto-create a new account.
type Step = 'email' | 'code';

export function LoginForm({ dict }: { dict: LoginDict }) {
  const search = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  // V2.11 (2026-05-27) — ToS gate. New users tick before submitting the
  // verification code; existing-user logins ignore the flag server-side.
  const [acceptTos, setAcceptTos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // TTL hint from /email/start so the UI can show "X 分钟内有效".
  const [ttlMin, setTtlMin] = useState<number>(10);
  // CRO-107 P3-9 (2026-06-17) — resend feedback + cooldown.
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    const err = search?.get('error');
    if (!err) return;
    const map: Record<string, string> = {
      missing_token: dict.common.err_missing_token,
      invalid_token: dict.common.err_invalid_token,
      token_used: dict.common.err_token_used,
      token_expired: dict.common.err_token_expired,
      user_missing: dict.common.err_user_missing,
      verify_failed: dict.common.err_verify_failed,
    };
    setError(map[err] ?? err);
  }, [search, dict]);

  // 2026-07-02 — defensive client-side re-check to fix the known
  // next-on-pages 1.13 cookies() miss. Server-side getMe() sometimes
  // returns null even when the session cookie is valid; the login page
  // then shows the form even though TopBar's TopBarClient defensively
  // re-fetches /api/me and shows the logged-in profile. Mirrors that
  // same client-side pattern here so a valid session redirects away
  // from /login the way it would if getMe() hadn't missed.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const me = await fetchMeClient();
      if (cancelled || !me?.user?.role) return;
      const role = me.user.role;
      const dest =
        role === 'pending' ? '/onboarding/role' : role === 'consumer' ? '/discover' : '/studio';
      // Full-page nav (not router.push) so the whole tree — including
      // TopBar seed — comes back consistent with the actual session.
      window.location.replace(dest);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function mapCodeError(code: string | undefined): string {
    if (code === 'code_not_found' || code === 'code_invalid')
      return dict.code_step.err_code_invalid;
    if (code === 'code_used') return dict.code_step.err_code_used;
    if (code === 'code_expired') return dict.code_step.err_code_expired;
    if (code === 'too_many_attempts') return dict.code_step.err_too_many_attempts;
    return code ?? dict.common.net_err;
  }

  async function submitEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    // 2026-06-17 Amy QA CRO-107 P2-8: pre-submit guards. Empty + obviously
    // malformed emails used to silently round-trip to the server and come
    // back as a generic 「server returned 400」 toast that Amy missed.
    // Surface the actual constraint inline so the user knows WHY the
    // 继续 click did nothing.
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(dict.email_step.err_email_required ?? '请输入邮箱');
      return;
    }
    // Conservative RFC-5321-ish check; no need to be RFC perfect — the
    // server still validates. We only need to catch obvious typos like
    // missing @ or domain so the user fixes them client-side.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(dict.email_step.err_email_invalid ?? '邮箱格式不正确，请检查后重试');
      return;
    }
    setPending(true);
    try {
      const res = await fetch('/api/auth/email/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        state?: string;
        error?: string;
        ttl_min?: number;
        retry_after_s?: number;
      };
      if (!res.ok) {
        // 2026-07-11 — previously we set the raw server error code
        // ("rate_limit_exceeded", "invalid_body", …) into the input's
        // error slot; a real user saw that English enum in prod on
        // her third OTP-start attempt. Map the known codes here and
        // keep the raw code only as a last-resort fallback for codes
        // we haven't localised yet.
        if (body.error === 'rate_limit_exceeded') {
          const retrySec = Math.max(body.retry_after_s ?? 3600, 60);
          const minutes = Math.max(1, Math.ceil(retrySec / 60));
          setError(dict.email_step.err_rate_limit.replace('{minutes}', String(minutes)));
        } else if (body.error === 'invalid_body') {
          setError(dict.email_step.err_email_invalid);
        } else {
          setError(dict.email_step.err_generic);
        }
        return;
      }
      if (body.state === 'code_sent') {
        if (typeof body.ttl_min === 'number') setTtlMin(body.ttl_min);
        setCode('');
        setStep('code');
      } else {
        setError(dict.common.net_err);
      }
    } catch {
      setError(dict.common.net_err);
    } finally {
      setPending(false);
    }
  }

  async function submitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    // 2026-06-23 — ToS gate moved to the code step. Server only enforces
    // for new-account creation; existing-user login ignores the flag.
    // Local gate keeps the UX honest for both paths.
    if (!acceptTos) {
      setError(dict.code_step.tos_required ?? '请勾选同意条款');
      return;
    }
    setPending(true);
    try {
      const res = await fetch('/api/auth/email/verify-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
          accept_tos: acceptTos,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        state?: string;
        error?: string;
        redirect_to?: string;
        signup?: boolean;
      };
      if (!res.ok) {
        // tos_required surfaces if the user somehow bypassed the local check.
        if (body.error === 'tos_required') {
          setError(dict.code_step.tos_required ?? '请先勾选同意条款');
          return;
        }
        setError(mapCodeError(body.error));
        return;
      }
      if (body.state === 'logged_in') {
        // Hard nav so the cookie set by the response actually sticks
        // on the next page load (next-on-pages occasionally serves
        // stale layouts from cache on client-side router.push).
        window.location.href = body.redirect_to ?? '/';
        return;
      }
      setError(dict.common.net_err);
    } catch {
      setError(dict.common.net_err);
    } finally {
      setPending(false);
    }
  }

  async function resendCode() {
    // 2026-06-17 Amy QA CRO-107 P3-9: 「没收到？重发」 used to silently
    // round-trip with zero feedback. Surface a「验证码已重发」 hint plus
    // a 30-second cooldown so the user can't spam the endpoint or wonder
    // whether anything happened.
    setError(null);
    setResendNotice(null);
    setCode('');
    setPending(true);
    try {
      const res = await fetch('/api/auth/email/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const body = (await res.json().catch(() => ({}))) as { ttl_min?: number; error?: string };
      if (!res.ok) {
        setError(body.error ?? dict.common.net_err);
        return;
      }
      if (typeof body.ttl_min === 'number') setTtlMin(body.ttl_min);
      setResendNotice(dict.code_step.resend_sent ?? '验证码已重发，请查收');
      // 30s cooldown — countdown driven by useEffect below.
      setResendCooldown(30);
    } catch {
      setError(dict.common.net_err);
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="page-in max-w-md mx-auto px-6 pt-20 pb-16">
      <div className="flex justify-center mb-10">
        <Link href="/" aria-label={dict.common.brand} className="inline-flex">
          <BrandLogo variant="vertical" heightPx={140} />
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-soft p-8 shadow-sm">
        {step === 'email' && (
          <>
            <h1 className="font-serif text-2xl text-ink mb-2">{dict.email_step.title}</h1>
            <p className="text-dusk text-sm mb-6">{dict.email_step.sub}</p>
            <form onSubmit={submitEmail} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-ink mb-1.5 font-medium">
                  {dict.email_step.label_email}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-soft bg-cream/50 text-base text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition"
                  placeholder={dict.email_step.placeholder_email}
                />
              </div>
              {error && <ErrorBox text={error} />}
              <SubmitButton
                pending={pending}
                label={dict.email_step.cta}
                pendingLabel={dict.common.submitting}
              />
            </form>
            <p className="text-center text-xs text-dusk mt-6">
              {dict.email_step.legacy_link_a}{' '}
              <Link href="/login/password" className="text-warmth hover:underline">
                {dict.email_step.legacy_link_b}
              </Link>
            </p>
          </>
        )}

        {step === 'code' && (
          <>
            <h1 className="font-serif text-2xl text-ink mb-2">{dict.code_step.title_v2}</h1>
            <p className="text-dusk text-sm mb-6">
              {dict.code_step.sub_v2_a} <strong>{email}</strong>
              {dict.code_step.sub_v2_b.replace('{ttl}', String(ttlMin))}
            </p>
            <form onSubmit={submitCode} className="space-y-4">
              <div>
                <label
                  htmlFor="verification-code"
                  className="block text-sm text-ink mb-1.5 font-medium"
                >
                  {dict.code_step.label_verification_code}
                </label>
                <input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 rounded-2xl border border-soft bg-cream/50 text-2xl text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition font-mono tracking-[0.4em] text-center"
                  placeholder="······"
                />
              </div>
              {/* 2026-06-23 — ToS gate moved here. Required for both
                  login and auto-signup; server only enforces for new
                  accounts but UX stays consistent. */}
              <label className="flex items-start gap-2.5 text-sm text-dusk leading-snug cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={acceptTos}
                  onChange={(e) => setAcceptTos(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 rounded border-soft text-gold-dark focus:ring-warmth/40 cursor-pointer"
                />
                <span>
                  {dict.code_step.tos_agree_a}
                  <Link
                    href="/terms"
                    target="_blank"
                    rel="noopener"
                    className="underline decoration-dotted underline-offset-2 hover:text-ink"
                  >
                    {dict.code_step.tos_terms}
                  </Link>
                  {dict.code_step.tos_and}
                  <Link
                    href="/privacy"
                    target="_blank"
                    rel="noopener"
                    className="underline decoration-dotted underline-offset-2 hover:text-ink"
                  >
                    {dict.code_step.tos_privacy}
                  </Link>
                  {dict.code_step.tos_agree_b}
                </span>
              </label>
              {error && <ErrorBox text={error} />}
              <SubmitButton
                pending={pending}
                label={dict.code_step.cta_verify}
                pendingLabel={dict.common.submitting}
              />
            </form>

            <p className="text-center text-xs text-dusk mt-6 flex justify-center gap-4">
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
                className="hover:text-ink transition"
              >
                {dict.code_step.back}
              </button>
              <button
                type="button"
                onClick={resendCode}
                disabled={pending || resendCooldown > 0}
                className="text-warmth hover:underline disabled:opacity-60 disabled:no-underline disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `${resendCooldown}s 后可重发` : dict.code_step.resend}
              </button>
            </p>
            {resendNotice && (
              <output aria-live="polite" className="block text-sage text-xs text-center mt-2">
                ✓ {resendNotice}
              </output>
            )}
          </>
        )}
      </div>

      {step === 'email' && (
        <p className="text-center text-xs text-dusk mt-8">
          <Link href="/" className="hover:text-ink transition">
            {dict.common.back_home}
          </Link>
        </p>
      )}
    </main>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <p
      role="alert"
      className="text-sm text-warmth bg-warmth/10 border border-warmth/20 rounded-2xl px-4 py-3"
    >
      {text}
    </p>
  );
}

function SubmitButton({
  pending,
  label,
  pendingLabel,
}: {
  pending: boolean;
  label: string;
  pendingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full bg-warmth-dark text-white font-medium px-4 py-3 rounded-2xl hover:bg-warmth transition disabled:opacity-60 disabled:cursor-not-allowed min-h-touch"
    >
      {pending ? pendingLabel : label}
    </button>
  );
}
